import React from "react";
import dayjs from "dayjs";
import { Session } from "../../../../Entities/Session";
import { InterviewRoundType } from "../../../../Dto/GenerateJobDescription.dto";
import { Button, Tag, Typography } from "antd";
import {
  ClockCircleOutlined,
  BarChartOutlined,
  QuestionCircleOutlined,
  PercentageOutlined,
  CheckCircleOutlined,
  HourglassOutlined,
  CalendarOutlined,
  AlertOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import SessionRecordingPlayer from "../SessionRecordingPlayer/SessionRecordingPlayer";

interface JobApplicationinterviewRoundsDetailsSummaryProps {
  session: Session;
  roundConfig?: InterviewRoundType; // Contains passPercentage & isMustQualify from job post
  handleViolationModal: () => void;
  handleScreenRecordingModal: () => void;
  passPercentage: number;
  isMustQualify: boolean;
  attemptNumber?: number;
  isRestarted?: boolean;
}

const JobApplicationinterviewRoundsDetailsSummary: React.FC<
  JobApplicationinterviewRoundsDetailsSummaryProps
> = ({
  session,
  roundConfig,
  passPercentage,
  isMustQualify,
  handleViolationModal,
  handleScreenRecordingModal,
}) => {
  const { activityLogs, missedPings, screenRecordingLink } = session;

  const filteredLogs = activityLogs?.filter(
    (a) => a.event !== "time_limit_reached",
  );
  const isShowViolations = !!(
    (filteredLogs && filteredLogs.length > 0) ||
    (missedPings && missedPings > 0)
  );

  const isExpired = activityLogs?.some(
    (log) => log.event === "session_expired",
  );

  const isTimeLimitReached = activityLogs?.some(
    (log) => log.event === "time_limit_reached",
  );

  const isShowRecording = !!(screenRecordingLink && screenRecordingLink !== "");

  const tagColor =
    session?.status === "qualified"
      ? "green"
      : session?.status === "unqualified"
        ? "red"
        : "blue";

  // Format time taken in hh:mm:ss format
  const formatTimeTaken = (): string => {
    if (!session.sessionEndedAt || !session.sessionStartedAt) {
      return "00:00:00";
    }

    const startTime = dayjs(session.sessionStartedAt);
    const endTime = dayjs(session.sessionEndedAt);
    const totalSeconds = endTime.diff(startTime, "second");

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Get time taken status for color coding
  const getTimeTakenStatus = (): "within" | "exceeded" | "warning" | null => {
    if (
      session.status === "pending" ||
      !session.sessionEndedAt ||
      !session.sessionStartedAt
    ) {
      return null;
    }

    const estimatedTimeMinutes = session.estimatedTime || 0;
    if (estimatedTimeMinutes === 0) {
      return null; // Can't compare if no estimated time
    }

    const startTime = dayjs(session.sessionStartedAt);
    const endTime = dayjs(session.sessionEndedAt);
    const timeTakenSeconds = endTime.diff(startTime, "second");
    const estimatedTimeSeconds = estimatedTimeMinutes * 60;

    // Calculate percentage of time used
    const timePercentage = (timeTakenSeconds / estimatedTimeSeconds) * 100;

    if (timePercentage <= 100) {
      // Within time limit
      if (timePercentage >= 90) {
        // Close to limit (90-100%) - warning
        return "warning";
      }
      return "within";
    } else {
      // Exceeded time limit
      return "exceeded";
    }
  };

  const timeTakenFormatted = formatTimeTaken();
  const timeTakenStatus = getTimeTakenStatus();

  return (
    <div className="job-application-interview-rounds-details-summary-container">
      <div className="job-application-interview-rounds-details-section-header">
        <Typography.Text strong>Summary</Typography.Text>
      </div>
      <div className="job-application-interview-rounds-details-summary-content">
        <div className="summary-metrics-grid">
          {/* Row 1 */}
          <div className="summary-metric-item">
            <BarChartOutlined className="summary-metric-icon" />
            <div className="summary-metric-content">
              <Typography.Text className="summary-metric-label">
                Round Status:
              </Typography.Text>
              <Tag color={tagColor} className="summary-metric-value-tag">
                {session.status.toUpperCase()}
              </Tag>
            </div>
          </div>

          <div className="summary-metric-item">
            <CheckCircleOutlined className="summary-metric-icon" />
            <div className="summary-metric-content">
              <Typography.Text className="summary-metric-label">
                Must Qualify:
              </Typography.Text>
              <Tag
                color={isMustQualify ? "green" : "orange"}
                className="summary-metric-value-tag"
              >
                {isMustQualify ? "YES" : "NO"}
              </Tag>
            </div>
          </div>

          <div className="summary-metric-item">
            <PercentageOutlined className="summary-metric-icon" />
            <div className="summary-metric-content">
              <Typography.Text className="summary-metric-label">
                Min. Pass %:
              </Typography.Text>
              <Tag color="blue" className="summary-metric-value-tag">
                {passPercentage}%
              </Tag>
            </div>
          </div>

          {/* Row 2 */}
          <div className="summary-metric-item">
            <QuestionCircleOutlined className="summary-metric-icon" />
            <div className="summary-metric-content">
              <Typography.Text className="summary-metric-label">
                Total Questions:
              </Typography.Text>
              <Typography.Text className="summary-metric-value-number">
                {session.noOfQuestionsGenerated || 0}
              </Typography.Text>
            </div>
          </div>

          <div className="summary-metric-item">
            <HourglassOutlined className="summary-metric-icon" />
            <div className="summary-metric-content">
              <Typography.Text className="summary-metric-label">
                Estimated Time:
              </Typography.Text>
              <Typography.Text className="summary-metric-value-number">
                {session.estimatedTime || 0} minutes
              </Typography.Text>
            </div>
          </div>

          <div className="summary-metric-item">
            {session.status !== "pending" ? (
              <>
                <ClockCircleOutlined className="summary-metric-icon" />
                <div className="summary-metric-content">
                  <Typography.Text className="summary-metric-label">
                    Time Taken:
                  </Typography.Text>
                  <span
                    className={`summary-metric-value-number ${
                      timeTakenStatus
                        ? `summary-metric-value-number--${timeTakenStatus}`
                        : ""
                    }`}
                    style={{
                      color:
                        timeTakenStatus === "within"
                          ? "#52c41a" // Ant Design success green
                          : timeTakenStatus === "warning"
                            ? "#faad14" // Ant Design warning orange
                            : timeTakenStatus === "exceeded"
                              ? "#ff4d4f" // Ant Design error red
                              : "rgba(0, 0, 0, 0.85)", // Default text color
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "1.4",
                      display: "inline-block",
                    }}
                  >
                    {timeTakenFormatted}
                  </span>
                </div>
              </>
            ) : (
              <div
                className="summary-metric-content"
                style={{ width: "100%" }}
              />
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="summary-divider" />

        {/* Footer */}
        <div className="summary-footer">
          <CalendarOutlined className="summary-footer-icon" />
          <Typography.Text className="summary-footer-text">
            Last updated :{" "}
            {dayjs(session.updatedAt).format("DD MMM, YYYY h:mm A")}
          </Typography.Text>
        </div>

        {isTimeLimitReached && (
          <div className="summary-violations-section">
            <Typography.Text
              className="summary-violations-label"
              style={{ color: "#ff4d4f" }}
            >
              Candidate’s allotted time for this round has expired
            </Typography.Text>
          </div>
        )}

        {/* Expired Unqualified Notice */}
        {isExpired && (
          <div className="summary-violations-section">
            <CloseCircleOutlined
              style={{ color: "#ff4d4f" }}
              className="summary-violations-icon"
            />
            <Typography.Text
              className="summary-violations-label"
              style={{ color: "#ff4d4f" }}
            >
              Marked unqualified due to session expiry
            </Typography.Text>
          </div>
        )}

        {/* Violations Section */}
        {isShowViolations && (
          <div className="summary-violations-section">
            <AlertOutlined className="summary-violations-icon" />
            <Typography.Text className="summary-violations-label">
              Violations:
            </Typography.Text>
            <Button
              size="small"
              onClick={handleViolationModal}
              danger
              type="default"
              className="summary-violations-button"
            >
              View
            </Button>
          </div>
        )}
      </div>

      {isShowRecording && (
        <>
          <div className="job-application-interview-rounds-details-section-header">
            <Typography.Text strong>Session Recording</Typography.Text>
          </div>
          <div className="job-application-interview-rounds-details-summary-video-player-container">
            <SessionRecordingPlayer
              videoUrl={screenRecordingLink}
              key={session._id}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default JobApplicationinterviewRoundsDetailsSummary;
